import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { blogPosts, getAllCategories, getFeaturedPosts } from './posts';
import BlogHero from '../../components/BlogHero';
import HexagonLogo from '../../components/HexagonLogo';
import '../../components/HexagonLogo.css';
import './Blog.css';

const Blog = () => {
  const [selectedCategory, setSelectedCategory] = useState(null);
  const categories = getAllCategories();
  const featuredPosts = getFeaturedPosts();

  // Filter posts based on selected category
  const filteredPosts = selectedCategory
    ? blogPosts.filter(p => p.category === selectedCategory)
    : blogPosts;

  const regularPosts = filteredPosts.filter(p => !p.featured);
  const displayFeatured = selectedCategory ? [] : featuredPosts;

  return (
    <>
      <Helmet>
        <title>Fantomize Blog — Visual Search, AI Tagging & Modern Shopping</title>
        <meta name="description" content="Explore how visual search, AI product tagging, and style-first discovery are transforming eCommerce. Insights for Shopify merchants and fashion-forward shoppers." />
        <meta name="keywords" content="Shopify visual search, image search Shopify app, AI product tagging Shopify, eCommerce tips, fashion technology" />
        <link rel="canonical" href="https://fantomize.com/blog" />

        {/* Open Graph */}
        <meta property="og:title" content="Fantomize Blog — Visual Search & AI Tagging" />
        <meta property="og:description" content="Explore how visual search and AI are transforming product discovery in eCommerce." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://fantomize.com/blog" />

        {/* Schema.org Blog */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Blog",
            "name": "Fantomize Blog",
            "description": "Visual search, AI tagging, and the future of product discovery",
            "url": "https://fantomize.com/blog",
            "publisher": {
              "@type": "Organization",
              "name": "Fantomize",
              "url": "https://fantomize.com"
            }
          })}
        </script>
      </Helmet>

      <div className="blog-page">
        {/* Hero Bar with Navigation */}
        <BlogHero
          breadcrumbs={[{ label: 'Home', to: '/' }]}
          currentPage="Blog"
          navLinks={[
            { label: 'Home', to: '/' },
            { label: 'Search', to: '/search' },
            { label: 'Text Search', to: '/text-search' }
          ]}
          showPattern={true}
        />

        <div className="blog-container">
          {/* Header */}
          <header className="blog-header">
            <span className="header-label">The Blog</span>
            <h1>Rethinking Product Discovery</h1>
            <p className="blog-subtitle">
              Exploring visual search, AI tagging, and the future of how we shop online
            </p>
          </header>

          {/* Featured Posts - Magazine Layout */}
          {displayFeatured.length > 0 && (
            <section className="featured-section">
              <div className="featured-grid">
                {displayFeatured.map((post, index) => (
                  <article
                    key={post.slug}
                    className={`featured-card ${index === 0 ? 'featured-main' : 'featured-secondary'}`}
                  >
                    {post.heroImage && (
                      <Link to={`/blog/${post.slug}`} className="featured-image-link">
                        <img
                          src={post.heroImage}
                          alt={post.heroAlt || post.title}
                          className="featured-image"
                        />
                        <div className="featured-image-overlay" />
                      </Link>
                    )}
                    <div className="featured-content">
                      <span className="featured-category">{post.category}</span>
                      <h2>
                        <Link to={`/blog/${post.slug}`}>{post.title}</Link>
                      </h2>
                      <p className="featured-excerpt">{post.excerpt}</p>
                      <div className="featured-meta">
                        <span className="featured-date">
                          {new Date(post.date).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric'
                          })}
                        </span>
                        <span className="featured-read-time">{post.readTime}</span>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            </section>
          )}

          {/* Categories */}
          <nav className="blog-categories">
            <span className="category-label">Topics</span>
            <button
              className={`category-tag ${selectedCategory === null ? 'category-tag-active' : ''}`}
              onClick={() => setSelectedCategory(null)}
            >
              All
            </button>
            {categories.map(category => (
              <button
                key={category}
                className={`category-tag ${selectedCategory === category ? 'category-tag-active' : ''}`}
                onClick={() => setSelectedCategory(category)}
              >
                {category}
              </button>
            ))}
          </nav>

          {/* Regular Posts Grid */}
          {(selectedCategory ? filteredPosts : regularPosts).length > 0 && (
            <section className="posts-section">
              <h2 className="section-title">
                {selectedCategory ? `${selectedCategory} Articles` : 'More Articles'}
              </h2>
              <div className="posts-grid">
                {(selectedCategory ? filteredPosts : regularPosts).map(post => (
                  <article key={post.slug} className="post-card">
                    {post.heroImage && (
                      <Link to={`/blog/${post.slug}`} className="post-image-link">
                        <img
                          src={post.heroImage}
                          alt={post.heroAlt || post.title}
                          className="post-image"
                        />
                      </Link>
                    )}
                    <div className="post-content">
                      <div className="post-category">{post.category}</div>
                      <h3>
                        <Link to={`/blog/${post.slug}`}>{post.title}</Link>
                      </h3>
                      <p className="post-excerpt">{post.excerpt}</p>
                      <div className="post-meta">
                        <span className="post-date">
                          {new Date(post.date).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric'
                          })}
                        </span>
                        <span className="post-read-time">{post.readTime}</span>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            </section>
          )}

          {/* Footer */}
          <footer className="blog-footer">
            <div className="footer-content">
              <div className="footer-brand">
                <HexagonLogo size={28} strokeWidth={3} />
                <span className="footer-tagline">Visual search for modern shopping</span>
              </div>
              <div className="footer-links">
                <Link to="/">Home</Link>
                <Link to="/blog">Blog</Link>
                <Link to="/search">Search</Link>
              </div>
            </div>
            <p className="copyright">&copy; 2026 Fantomize. All rights reserved.</p>
          </footer>
        </div>
      </div>
    </>
  );
};

export default Blog;


